import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ethers } from "ethers";
import { connectToContract, listenToEvents } from "./CrownContract";
import CrownABI from "../abi/Crown.json";
import { FaHistory } from "react-icons/fa";

const crownAddress = "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03";

const Transactions = ({ provider, account }) => {
  const [activeTab, setActiveTab] = useState("all");
  const [allTransactions, setAllTransactions] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [currentPageAll, setCurrentPageAll] = useState(1);
  const [currentPageUser, setCurrentPageUser] = useState(1);
  const transactionsPerPage = 6;

  const indexOfLastAll = currentPageAll * transactionsPerPage;
  const indexOfFirstAll = indexOfLastAll - transactionsPerPage;
  const indexOfLastUser = currentPageUser * transactionsPerPage;
  const indexOfFirstUser = indexOfLastUser - transactionsPerPage;

  const addTransaction = useCallback(
    (tx) => {
      console.log("Adding Crown transaction:", {
        name: tx.name,
        transactionHash: tx.transactionHash,
        blockNumber: tx.blockNumber,
        timestamp: tx.timestamp,
      });
      let formattedTx = {
        id: tx.transactionHash || Date.now().toString(),
        type: tx.name,
        from: "Unknown",
        to: "Unknown",
        amount: "Unknown",
        blockNumber: tx.blockNumber || "Pending",
        timestamp: tx.timestamp ? tx.timestamp * 1000 : "Pending",
        displayTime: tx.timestamp
          ? new Date(tx.timestamp * 1000).toLocaleString()
          : "Pending",
        details: "Unknown",
        contract: "Crown",
      };

      const data = tx.data;
      if (tx.name === "Transfer") {
        formattedTx.from = data.from || "Unknown";
        formattedTx.to = data.to || "Unknown";
        formattedTx.amount = data.value ? ethers.formatEther(data.value) : "Unknown";
        formattedTx.details = `From: ${formattedTx.from}, To: ${formattedTx.to}, Value: ${formattedTx.amount}`;
      } else if (tx.name === "Staked") {
        formattedTx.from = data.staker || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `Staker: ${formattedTx.from}, Amount Staked: ${formattedTx.amount}`;
      } else if (tx.name === "Unstaked") {
        formattedTx.from = data.staker || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `Staker: ${formattedTx.from}, Amount Unstaked: ${formattedTx.amount}`;
      } else if (tx.name === "RewardsClaimed") {
        formattedTx.from = data.staker || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `Staker: ${formattedTx.from}, Rewards Claimed: ${formattedTx.amount}`;
      } else if (tx.name === "UserLoanCreated") {
        formattedTx.from = data.lender || "Unknown";
        formattedTx.to = data.borrower && data.borrower !== "0x0000000000000000000000000000000000000000" ? data.borrower : "Contract";
        formattedTx.amount = data.loanAmount ? ethers.formatEther(data.loanAmount) : "Unknown";
        formattedTx.details = `Lender: ${formattedTx.from}, Borrower: ${formattedTx.to}, Loan Amount: ${formattedTx.amount}, Interest Rate: ${data.interestRate}%, Duration: ${data.loanDuration} seconds`;
      } else if (tx.name === "UserLoanActivated") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.loanAmount ? ethers.formatEther(data.loanAmount) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Collateral: ${data.collateralAmount ? ethers.formatEther(data.collateralAmount) : "Unknown"}, Loan Amount: ${formattedTx.amount}`;
      } else if (tx.name === "UserLoanRepaid") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = data.lender || "Unknown";
        formattedTx.amount = data.totalRepaymentAmount ? ethers.formatEther(data.totalRepaymentAmount) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Lender: ${formattedTx.to}, Total Repayment: ${formattedTx.amount}, Remaining Collateral: ${data.remainingCollateral ? ethers.formatEther(data.remainingCollateral) : "Unknown"}`;
      } else if (tx.name === "UserLoanLiquidated") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = data.lender || "Unknown";
        formattedTx.amount = data.totalCollateral ? ethers.formatEther(data.totalCollateral) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Lender: ${formattedTx.to}, Total Collateral: ${formattedTx.amount}, Lender Share: ${data.lenderShare ? ethers.formatEther(data.lenderShare) : "Unknown"}, Owner Share: ${data.ownerShare ? ethers.formatEther(data.ownerShare) : "Unknown"}, Reward Pool Share: ${data.rewardPoolShare ? ethers.formatEther(data.rewardPoolShare) : "Unknown"}`;
      } else if (tx.name === "UserLoanCancelled") {
        formattedTx.from = data.lender || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Lender: ${formattedTx.from}, Loan Cancelled`;
      } else if (tx.name === "LoanCreated") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.loanAmount ? ethers.formatEther(data.loanAmount) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Collateral: ${data.collateralWei ? ethers.formatEther(data.collateralWei) : "Unknown"} ETH, Loan Amount: ${formattedTx.amount}`;
      } else if (tx.name === "LoanRepaid") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.totalRepayment ? ethers.formatEther(data.totalRepayment) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Collateral Returned: ${data.collateralReturned ? ethers.formatEther(data.collateralReturned) : "Unknown"} ETH, Total Repayment: ${formattedTx.amount}`;
      } else if (tx.name === "CollateralLiquidated") {
        formattedTx.from = data.borrower || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.collateralWei ? ethers.formatEther(data.collateralWei) : "Unknown";
        formattedTx.details = `Borrower: ${formattedTx.from}, Collateral Liquidated: ${formattedTx.amount} ETH, Reserve Share: ${data.reserveShareInWei ? ethers.formatEther(data.reserveShareInWei) : "Unknown"} ETH`;
      } else if (tx.name === "ValidatorAdded") {
        formattedTx.from = data.validatorAddress || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.stakedAmount ? ethers.formatEther(data.stakedAmount) : "Unknown";
        formattedTx.details = `Validator: ${formattedTx.from}, Staked Amount: ${formattedTx.amount}`;
      } else if (tx.name === "ValidatorRemoved") {
        formattedTx.from = data.validatorAddress || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Validator: ${formattedTx.from}, Validator Removed`;
      } else if (tx.name === "RewardsWithdrawn") {
        formattedTx.from = data.validator || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `Validator: ${formattedTx.from}, Rewards Withdrawn: ${formattedTx.amount}`;
      } else if (tx.name === "TransactionFeeRateUpdated") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Old Rate: ${data.oldRate ? Number(data.oldRate) / 100 : "Unknown"}%, New Rate: ${data.newRate ? Number(data.newRate) / 100 : "Unknown"}%`;
      } else if (tx.name === "UseOraclePriceUpdated") {
        formattedTx.from = data.owner || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Owner: ${formattedTx.from}, Use Oracle Price: ${data.newStatus}`;
      } else if (tx.name === "CrwPriceFeedUpdated") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Old Feed: ${data.oldFeed}, New Feed: ${data.newFeed}`;
      } else if (tx.name === "TokensBought") {
        formattedTx.from = data.buyer || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.tokensBought ? ethers.formatEther(data.tokensBought) : "Unknown";
        formattedTx.details = `Buyer: ${formattedTx.from}, ETH: ${data.ethAmountInWei ? ethers.formatEther(data.ethAmountInWei) : "Unknown"}, Tokens: ${formattedTx.amount}, Fee: ${data.feeInTokens ? ethers.formatEther(data.feeInTokens) : "Unknown"}, Owner Fee: ${data.ownerFee ? ethers.formatEther(data.ownerFee) : "Unknown"}, Reward Pool Fee: ${data.rewardPoolFee ? ethers.formatEther(data.rewardPoolFee) : "Unknown"}, Validators Fee: ${data.validatorsFee ? ethers.formatEther(data.validatorsFee) : "Unknown"}`;
      } else if (tx.name === "TokensSold") {
        formattedTx.from = data.seller || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.tokenAmount ? ethers.formatEther(data.tokenAmount) : "Unknown";
        formattedTx.details = `Seller: ${formattedTx.from}, Tokens: ${formattedTx.amount}, ETH Received: ${data.ethReceived ? ethers.formatEther(data.ethReceived) : "Unknown"}, Fee: ${data.feeInTokens ? ethers.formatEther(data.feeInTokens) : "Unknown"}, Owner Fee: ${data.ownerFee ? ethers.formatEther(data.ownerFee) : "Unknown"}, Reward Pool Fee: ${data.rewardPoolFee ? ethers.formatEther(data.rewardPoolFee) : "Unknown"}, Validators Fee: ${data.validatorsFee ? ethers.formatEther(data.validatorsFee) : "Unknown"}`;
      } else if (tx.name === "TokensBoughtWithERC20") {
        formattedTx.from = data.buyer || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.tokensBought ? ethers.formatEther(data.tokensBought) : "Unknown";
        formattedTx.details = `Buyer: ${formattedTx.from}, Token: ${data.token}, Token Amount: ${data.tokenAmount ? ethers.formatEther(data.tokenAmount) : "Unknown"}, Tokens Bought: ${formattedTx.amount}`;
      } else if (tx.name === "TokensSoldForERC20") {
        formattedTx.from = data.seller || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.tokenAmount ? ethers.formatEther(data.tokenAmount) : "Unknown";
        formattedTx.details = `Seller: ${formattedTx.from}, Token: ${data.token}, Tokens Sold: ${formattedTx.amount}, ERC20 Received: ${data.erc20Received ? ethers.formatEther(data.erc20Received) : "Unknown"}`;
      } else if (tx.name === "TokenAdded") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Token: ${data.token}, Price Feed: ${data.priceFeed}, Decimals: ${data.tokenDecimals}, Heartbeat: ${data.heartbeat}`;
      } else if (tx.name === "TokenRemoved") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Token: ${data.token}`;
      } else if (tx.name === "TokenUpdated") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Token: ${data.token}, New Price Feed: ${data.newPriceFeed}, New Heartbeat: ${data.newHeartbeat}`;
      } else if (tx.name === "RewardPoolFeeTransferred") {
        formattedTx.from = data.from || "Unknown";
        formattedTx.to = "Contract";
        formattedTx.amount = data.feeAmount ? ethers.formatEther(data.feeAmount) : "Unknown";
        formattedTx.details = `From: ${formattedTx.from}, Fee Amount: ${formattedTx.amount}`;
      } else if (tx.name === "ValidatorFeesDistributed") {
        formattedTx.from = "Contract";
        formattedTx.to = "Validators";
        formattedTx.amount = data.totalValidatorFee ? ethers.formatEther(data.totalValidatorFee) : "Unknown";
        formattedTx.details = `Total Validator Fee: ${formattedTx.amount}, Individual Share: ${data.individualValidatorShare ? ethers.formatEther(data.individualValidatorShare) : "Unknown"}`;
      } else if (tx.name === "Approval") {
        formattedTx.from = data.owner || "Unknown";
        formattedTx.to = data.spender || "Unknown";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `Owner: ${formattedTx.from}, Spender: ${formattedTx.to}, Amount Approved: ${formattedTx.amount}`;
      } else if (tx.name === "DailyRewardRateUpdated") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `Old Rate: ${data.oldRate ? Number(data.oldRate) / 100 : "Unknown"}%, New Rate: ${data.newRate ? Number(data.newRate) / 100 : "Unknown"}%`;
      } else if (tx.name === "EthTransferAttempted") {
        formattedTx.from = "Contract";
        formattedTx.to = data.to || "Unknown";
        formattedTx.amount = data.amount ? ethers.formatEther(data.amount) : "Unknown";
        formattedTx.details = `To: ${formattedTx.to}, Amount: ${formattedTx.amount} ETH, Success: ${data.success}`;
      } else if (tx.name === "DailyInterestRateUpdated") {
        formattedTx.from = "Owner";
        formattedTx.to = "Contract";
        formattedTx.amount = "0";
        formattedTx.details = `New Rate: ${data.newRate ? Number(data.newRate) / 100 : "Unknown"}%`;
      }

      setAllTransactions((prev) => {
        const updated = [formattedTx, ...prev.filter((t) => t.id !== formattedTx.id)];
        return updated.sort((a, b) =>
          b.timestamp === "Pending" ? 1 : a.timestamp === "Pending" ? -1 : b.timestamp - a.timestamp
        );
      });

      if (
        account &&
        (formattedTx.from?.toLowerCase() === account.toLowerCase() ||
          (formattedTx.to !== "Contract" && formattedTx.to !== "Validators" && formattedTx.to?.toLowerCase() === account.toLowerCase()))
      ) {
        setUserTransactions((prev) => {
          const updated = [formattedTx, ...prev.filter((t) => t.id !== formattedTx.id)];
          return updated.sort((a, b) =>
            b.timestamp === "Pending" ? 1 : a.timestamp === "Pending" ? -1 : b.timestamp - a.timestamp
          );
        });
      }
    },
    [account]
  );

  const filteredAllTransactions = useMemo(
    () => allTransactions.slice(indexOfFirstAll, indexOfLastAll),
    [allTransactions, indexOfFirstAll, indexOfLastAll]
  );
  const filteredUserTransactions = useMemo(
    () => userTransactions.slice(indexOfFirstUser, indexOfLastUser),
    [userTransactions, indexOfFirstUser, indexOfLastUser]
  );

  useEffect(() => {
    if (!provider) {
      console.log("Provider is not available");
      return;
    }

    console.log("useEffect triggered with account:", account);

    const setupContracts = async () => {
      try {
        const crownContract = await connectToContract(provider);
        if (!crownContract) {
          throw new Error("Failed to connect to Crown contract");
        }
        console.log("Crown contract connected at address:", crownContract.address || "undefined");
        const crownCleanup = await listenToEvents(provider, addTransaction);

        const fromBlock = 12345678;
        const latestBlock = await provider.getBlockNumber();
        console.log(`Fetching past Crown contract events from block ${fromBlock} to ${latestBlock}...`);
        const contractWithProvider = new ethers.Contract(crownAddress, CrownABI, provider);

        const events = [
          { name: "Transfer", filter: contractWithProvider.filters.Transfer() },
          { name: "Staked", filter: contractWithProvider.filters.Staked() },
          { name: "Unstaked", filter: contractWithProvider.filters.Unstaked() },
          { name: "RewardsClaimed", filter: contractWithProvider.filters.RewardsClaimed() },
          { name: "UserLoanCreated", filter: contractWithProvider.filters.UserLoanCreated() },
          { name: "UserLoanActivated", filter: contractWithProvider.filters.UserLoanActivated() },
          { name: "UserLoanRepaid", filter: contractWithProvider.filters.UserLoanRepaid() },
          { name: "UserLoanLiquidated", filter: contractWithProvider.filters.UserLoanLiquidated() },
          { name: "UserLoanCancelled", filter: contractWithProvider.filters.UserLoanCancelled() },
          { name: "LoanCreated", filter: contractWithProvider.filters.LoanCreated() },
          { name: "LoanRepaid", filter: contractWithProvider.filters.LoanRepaid() },
          { name: "CollateralLiquidated", filter: contractWithProvider.filters.CollateralLiquidated() },
          { name: "ValidatorAdded", filter: contractWithProvider.filters.ValidatorAdded() },
          { name: "ValidatorRemoved", filter: contractWithProvider.filters.ValidatorRemoved() },
          { name: "RewardsWithdrawn", filter: contractWithProvider.filters.RewardsWithdrawn() },
          { name: "TransactionFeeRateUpdated", filter: contractWithProvider.filters.TransactionFeeRateUpdated() },
          { name: "UseOraclePriceUpdated", filter: contractWithProvider.filters.UseOraclePriceUpdated() },
          { name: "CrwPriceFeedUpdated", filter: contractWithProvider.filters.CrwPriceFeedUpdated() },
          { name: "TokensBought", filter: contractWithProvider.filters.TokensBought() },
          { name: "TokensSold", filter: contractWithProvider.filters.TokensSold() },
          { name: "TokensBoughtWithERC20", filter: contractWithProvider.filters.TokensBoughtWithERC20() },
          { name: "TokensSoldForERC20", filter: contractWithProvider.filters.TokensSoldForERC20() },
          { name: "TokenAdded", filter: contractWithProvider.filters.TokenAdded() },
          { name: "TokenRemoved", filter: contractWithProvider.filters.TokenRemoved() },
          { name: "TokenUpdated", filter: contractWithProvider.filters.TokenUpdated() },
          { name: "RewardPoolFeeTransferred", filter: contractWithProvider.filters.RewardPoolFeeTransferred() },
          { name: "ValidatorFeesDistributed", filter: contractWithProvider.filters.ValidatorFeesDistributed() },
          { name: "Approval", filter: contractWithProvider.filters.Approval() },
          { name: "DailyRewardRateUpdated", filter: contractWithProvider.filters.DailyRewardRateUpdated() },
          { name: "EthTransferAttempted", filter: contractWithProvider.filters.EthTransferAttempted() },
          { name: "DailyInterestRateUpdated", filter: contractWithProvider.filters.DailyInterestRateUpdated() },
        ];

        for (const { name, filter } of events) {
          console.log(`Fetching ${name} events for Crown...`);
          const eventList = await contractWithProvider.queryFilter(filter, fromBlock, latestBlock);
          console.log(`Fetched ${name} events for Crown: ${eventList.length}`);
          if (eventList.length === 0) {
            console.log(`No ${name} events found. Check contract address, ABI, or block range.`);
          }
          for (const event of eventList) {
            const block = await provider.getBlock(event.blockNumber);
            console.log(`Processing ${name} event:`, {
              transactionHash: event.transactionHash,
              blockNumber: event.blockNumber,
              args: event.args,
            });
            let data;
            switch (name) {
              case "Transfer":
                data = { from: event.args.from, to: event.args.to, value: event.args.value };
                break;
              case "Staked":
                data = { staker: event.args.staker, amount: event.args.amountStaked };
                break;
              case "Unstaked":
                data = { staker: event.args.staker, amount: event.args.amountUnstaked };
                break;
              case "RewardsClaimed":
                data = { staker: event.args.staker, amount: event.args.rewardsClaimed };
                break;
              case "UserLoanCreated":
                data = { lender: event.args.lender, borrower: event.args.borrower, loanAmount: event.args.loanAmount, interestRate: event.args.interestRate, loanDuration: event.args.loanDuration };
                break;
              case "UserLoanActivated":
                data = { borrower: event.args.borrower, collateralAmount: event.args.collateralAmount, loanAmount: event.args.loanAmount };
                break;
              case "UserLoanRepaid":
                data = { borrower: event.args.borrower, lender: event.args.lender, totalRepaymentAmount: event.args.totalRepaymentAmount, remainingCollateral: event.args.remainingCollateral };
                break;
              case "UserLoanLiquidated":
                data = { borrower: event.args.borrower, lender: event.args.lender, totalCollateral: event.args.totalCollateral, lenderShare: event.args.lenderShare, ownerShare: event.args.ownerShare, rewardPoolShare: event.args.rewardPoolShare, liquidationTimestamp: event.args.liquidationTimestamp };
                break;
              case "UserLoanCancelled":
                data = { lender: event.args.lender };
                break;
              case "LoanCreated":
                data = { borrower: event.args.borrower, collateralWei: event.args.collateralWei, loanAmount: event.args.loanAmount };
                break;
              case "LoanRepaid":
                data = { borrower: event.args.borrower, collateralReturned: event.args.collateralReturned, totalRepayment: event.args.totalRepayment };
                break;
              case "CollateralLiquidated":
                data = { borrower: event.args.borrower, collateralWei: event.args.collateralWei, reserveShareInWei: event.args.reserveShareInWei };
                break;
              case "ValidatorAdded":
                data = { validatorAddress: event.args.validatorAddress, stakedAmount: event.args.stakedAmount };
                break;
              case "ValidatorRemoved":
                data = { validatorAddress: event.args.validatorAddress };
                break;
              case "RewardsWithdrawn":
                data = { validator: event.args.validator, amount: event.args.amount };
                break;
              case "TransactionFeeRateUpdated":
                data = { oldRate: event.args.oldRate, newRate: event.args.newRate };
                break;
              case "UseOraclePriceUpdated":
                data = { owner: event.args.owner, newStatus: event.args.newStatus };
                break;
              case "CrwPriceFeedUpdated":
                data = { oldFeed: event.args.oldFeed, newFeed: event.args.newFeed };
                break;
              case "TokensBought":
                data = { buyer: event.args.buyer, ethAmountInWei: event.args.ethAmountInWei, tokensBought: event.args.tokensBought, feeInTokens: event.args.feeInTokens, ownerFee: event.args.ownerFee, rewardPoolFee: event.args.rewardPoolFee, validatorsFee: event.args.validatorsFee, contractEthBalance: event.args.contractEthBalance };
                break;
              case "TokensSold":
                data = { seller: event.args.seller, tokenAmount: event.args.tokenAmount, ethReceived: event.args.ethReceived, feeInTokens: event.args.feeInTokens, ownerFee: event.args.ownerFee, rewardPoolFee: event.args.rewardPoolFee, validatorsFee: event.args.validatorsFee, contractEthBalance: event.args.contractEthBalance };
                break;
              case "TokensBoughtWithERC20":
                data = { buyer: event.args.buyer, token: event.args.token, tokenAmount: event.args.tokenAmount, tokensBought: event.args.tokensBought };
                break;
              case "TokensSoldForERC20":
                data = { seller: event.args.seller, token: event.args.token, tokenAmount: event.args.tokenAmount, erc20Received: event.args.erc20Received };
                break;
              case "TokenAdded":
                data = { token: event.args.token, priceFeed: event.args.priceFeed, tokenDecimals: event.args.tokenDecimals, heartbeat: event.args.heartbeat };
                break;
              case "TokenRemoved":
                data = { token: event.args.token };
                break;
              case "TokenUpdated":
                data = { token: event.args.token, newPriceFeed: event.args.newPriceFeed, newHeartbeat: event.args.newHeartbeat };
                break;
              case "RewardPoolFeeTransferred":
                data = { from: event.args.from, feeAmount: event.args.feeAmount };
                break;
              case "ValidatorFeesDistributed":
                data = { totalValidatorFee: event.args.totalValidatorFee, individualValidatorShare: event.args.individualValidatorShare };
                break;
              case "Approval":
                data = { owner: event.args.owner, spender: event.args.spender, amount: event.args.amount };
                break;
              case "DailyRewardRateUpdated":
                data = { oldRate: event.args.oldRate, newRate: event.args.newRate };
                break;
              case "EthTransferAttempted":
                data = { to: event.args.to, amount: event.args.amount, success: event.args.success };
                break;
              case "DailyInterestRateUpdated":
                data = { newRate: event.args.newRate };
                break;
              default:
                console.error(`Unhandled event type: ${name}`);
                continue;
            }
            addTransaction({ name, data, transactionHash: event.transactionHash, blockNumber: event.blockNumber, timestamp: block.timestamp });
          }
        }

        return () => {
          console.log("Cleaning up listeners for Crown contract");
          crownCleanup();
        };
      } catch (error) {
        console.error("Error setting up contracts:", error.message);
        throw error;
      }
    };

    let cleanupFn;
    setupContracts()
      .then((cleanup) => {
        cleanupFn = cleanup;
        console.log("Setup completed for Crown contract");
      })
      .catch((error) => {
        console.error("Setup failed:", error.message);
      });

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [provider, addTransaction]);

  const totalPagesAll = Math.ceil(allTransactions.length / transactionsPerPage);
  const totalPagesUser = Math.ceil(userTransactions.length / transactionsPerPage);

  const paginate = (tab, pageNumber) => {
    if (tab === "all") setCurrentPageAll(pageNumber);
    else setCurrentPageUser(pageNumber);
  };

  return (
    <div>
      <h2>
        <FaHistory className="icon" /> Transactions
      </h2>
      <div className="tab-container">
        <button
          className={activeTab === "all" ? "active-tab" : ""}
          onClick={() => setActiveTab("all")}
        >
          All Transactions
        </button>
        <button
          className={activeTab === "user" ? "active-tab" : ""}
          onClick={() => setActiveTab("user")}
        >
          User Transactions
        </button>
      </div>

      {activeTab === "all" && (
        <div className="action-box">
          <h3>All Transactions</h3>
          {filteredAllTransactions.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Hash</th>
                    <th>Contract</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Block</th>
                    <th>Time</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAllTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{tx.id.slice(0, 10)}...</td>
                      <td>{tx.contract}</td>
                      <td>{tx.type}</td>
                      <td>
                        {typeof tx.from === "string"
                          ? `${tx.from.slice(0, 6)}...${tx.from.slice(-4)}`
                          : "Unknown"}
                      </td>
                      <td>
                        {typeof tx.to === "string"
                          ? `${tx.to.slice(0, 6)}...${tx.to.slice(-4)}`
                          : "Unknown"}
                      </td>
                      <td>{tx.amount}</td>
                      <td>{tx.blockNumber}</td>
                      <td>{tx.displayTime}</td>
                      <td>{tx.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pagination">
                <button
                  onClick={() => paginate("all", currentPageAll - 1)}
                  disabled={currentPageAll === 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPageAll} of {totalPagesAll}
                </span>
                <button
                  onClick={() => paginate("all", currentPageAll + 1)}
                  disabled={currentPageAll === totalPagesAll}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <p>No transactions found.</p>
          )}
        </div>
      )}

      {activeTab === "user" && (
        <div className="action-box">
          <h3>User Transactions</h3>
          {filteredUserTransactions.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Hash</th>
                    <th>Contract</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Block</th>
                    <th>Time</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUserTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{tx.id.slice(0, 10)}...</td>
                      <td>{tx.contract}</td>
                      <td>{tx.type}</td>
                      <td>
                        {typeof tx.from === "string"
                          ? `${tx.from.slice(0, 6)}...${tx.from.slice(-4)}`
                          : "Unknown"}
                      </td>
                      <td>
                        {typeof tx.to === "string"
                          ? `${tx.to.slice(0, 6)}...${tx.to.slice(-4)}`
                          : "Unknown"}
                      </td>
                      <td>{tx.amount}</td>
                      <td>{tx.blockNumber}</td>
                      <td>{tx.displayTime}</td>
                      <td>{tx.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pagination">
                <button
                  onClick={() => paginate("user", currentPageUser - 1)}
                  disabled={currentPageUser === 1}
                >
                  Previous
                </button>
                <span>
                  Page {currentPageUser} of {totalPagesUser}
                </span>
                <button
                  onClick={() => paginate("user", currentPageUser + 1)}
                  disabled={currentPageUser === totalPagesUser}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <p>No user transactions found.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Transactions;